import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  ALL_PLATFORMS,
  PLATFORM_META,
  type PlanEntry,
  type Platform,
  type PoolIdea,
  type Storyboard,
  type WorkspaceReelLean,
} from "../api";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  showToast: ToastFn;
};

/** Generate a 7-day window starting on Monday of `anchor`. */
function weekDays(anchor: Date): Date[] {
  const day = anchor.getDay(); // 0 = Sun, 1 = Mon, ...
  const monOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + monOffset);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export const Plan: React.FC<Props> = ({ showToast }) => {
  const [entries, setEntries] = useState<PlanEntry[]>([]);
  const [ideas, setIdeas] = useState<PoolIdea[]>([]);
  const [storyboards, setStoryboards] = useState<Storyboard[]>([]);
  const [reels, setReels] = useState<WorkspaceReelLean[]>([]);
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftDate, setDraftDate] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const [pl, ip, sb, ws] = await Promise.all([
        api.planList(),
        api.ideasPoolList().catch(() => []),
        api.storyboardsList().catch(() => []),
        api.workspaceList().catch(() => []),
      ]);
      setEntries(pl);
      setIdeas(ip);
      setStoryboards(sb);
      setReels(ws);
    } catch (e) {
      showToast("error", `Plan-Load fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const days = useMemo(() => weekDays(anchor), [anchor]);
  const byDay = useMemo<Record<string, PlanEntry[]>>(() => {
    const out: Record<string, PlanEntry[]> = {};
    for (const e of entries) {
      const k = e.dateISO;
      out[k] = out[k] ?? [];
      out[k].push(e);
    }
    return out;
  }, [entries]);

  const editing = entries.find((e) => e.id === editingId) ?? null;

  const onCreateForDay = (d: Date): void => {
    setEditingId(null);
    setDraftDate(isoDay(d));
  };

  const onSave = async (payload: Partial<PlanEntry>): Promise<void> => {
    try {
      const saved = await api.planSave(payload);
      setEditingId(saved.id);
      setDraftDate(null);
      await load();
    } catch (e) {
      showToast("error", `Speichern fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!window.confirm("Eintrag löschen?")) return;
    await api.planDelete(id);
    setEditingId(null);
    await load();
  };

  const formattedRange = `${days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Content-Planung</h2>
          <p className="workspace-sub">Wann erscheint was — auf welchen Plattformen.</p>
        </div>
        <div className="workspace-head-actions">
          <button
            className="btn ghost"
            onClick={() => {
              const d = new Date(anchor);
              d.setDate(d.getDate() - 7);
              setAnchor(d);
            }}
          >
            ← Vorherige Woche
          </button>
          <span style={{ alignSelf: "center", opacity: 0.8 }}>{formattedRange}</span>
          <button className="btn ghost" onClick={() => setAnchor(new Date())}>
            Heute
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              const d = new Date(anchor);
              d.setDate(d.getDate() + 7);
              setAnchor(d);
            }}
          >
            Nächste Woche →
          </button>
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            {loading ? "…" : "↻"}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 8,
          marginTop: 16,
        }}
      >
        {days.map((d, i) => {
          const k = isoDay(d);
          const list = byDay[k] ?? [];
          const isToday = isoDay(new Date()) === k;
          return (
            <div
              key={k}
              style={{
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${isToday ? "#3b82f6" : "rgba(255,255,255,0.1)"}`,
                borderRadius: 12,
                padding: 8,
                minHeight: 240,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <strong style={{ fontSize: 13, opacity: 0.8 }}>
                  {DAY_LABELS[i]} {d.getDate()}.
                </strong>
                <button
                  className="btn ghost sm"
                  onClick={() => onCreateForDay(d)}
                  title="Eintrag hinzufügen"
                  style={{ padding: "2px 6px" }}
                >
                  ＋
                </button>
              </div>
              {list.length === 0 ? (
                <div style={{ opacity: 0.4, fontSize: 12, marginTop: 8 }}>—</div>
              ) : (
                list.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => {
                      setEditingId(e.id);
                      setDraftDate(null);
                    }}
                    style={{
                      textAlign: "left",
                      background: editingId === e.id ? "rgba(59,130,246,0.18)" : "rgba(0,0,0,0.2)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 8,
                      padding: 6,
                      color: "inherit",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    {e.time ? <div style={{ opacity: 0.7 }}>{e.time}</div> : null}
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {labelForEntry(e, ideas, storyboards, reels)}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {e.channels.map((c) => (
                        <span
                          key={c}
                          title={PLATFORM_META[c].label}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 18,
                            height: 18,
                            background: PLATFORM_META[c].color,
                            color: "white",
                            borderRadius: 4,
                            fontSize: 10,
                          }}
                        >
                          {PLATFORM_META[c].icon}
                        </span>
                      ))}
                    </div>
                  </button>
                ))
              )}
            </div>
          );
        })}
      </div>

      {(editing || draftDate) && (
        <PlanEditor
          key={editing?.id ?? draftDate ?? "new"}
          entry={editing}
          draftDate={draftDate}
          ideas={ideas}
          storyboards={storyboards}
          reels={reels}
          onClose={() => {
            setEditingId(null);
            setDraftDate(null);
          }}
          onSave={onSave}
          onDelete={editing ? () => void onDelete(editing.id) : undefined}
        />
      )}
    </div>
  );
};

function labelForEntry(
  e: PlanEntry,
  ideas: PoolIdea[],
  storyboards: Storyboard[],
  reels: WorkspaceReelLean[],
): string {
  if (e.reelId) return reels.find((r) => r.id === e.reelId)?.title ?? `Reel ${e.reelId.slice(0, 8)}`;
  if (e.storyboardId) return storyboards.find((s) => s.id === e.storyboardId)?.title ?? `Storyboard ${e.storyboardId.slice(0, 8)}`;
  if (e.ideaId) return ideas.find((i) => i.id === e.ideaId)?.title ?? `Idee ${e.ideaId.slice(0, 8)}`;
  return e.note?.trim() || "Eintrag";
}

type EditorProps = {
  entry: PlanEntry | null;
  draftDate: string | null;
  ideas: PoolIdea[];
  storyboards: Storyboard[];
  reels: WorkspaceReelLean[];
  onClose: () => void;
  onSave: (payload: Partial<PlanEntry>) => void | Promise<void>;
  onDelete?: () => void;
};

const PlanEditor: React.FC<EditorProps> = ({ entry, draftDate, ideas, storyboards, reels, onClose, onSave, onDelete }) => {
  const [dateISO, setDateISO] = useState(entry?.dateISO ?? draftDate ?? new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(entry?.time ?? "");
  const [ideaId, setIdeaId] = useState<string>(entry?.ideaId ?? "");
  const [storyboardId, setStoryboardId] = useState<string>(entry?.storyboardId ?? "");
  const [reelId, setReelId] = useState<string>(entry?.reelId ?? "");
  const [channels, setChannels] = useState<Platform[]>(entry?.channels ?? []);
  const [note, setNote] = useState(entry?.note ?? "");

  const toggleChannel = (p: Platform): void => {
    setChannels((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const submit = (): void => {
    void onSave({
      ...(entry ? { id: entry.id } : {}),
      dateISO,
      time: time.trim() || undefined,
      ideaId: ideaId || undefined,
      storyboardId: storyboardId || undefined,
      reelId: reelId || undefined,
      channels,
      note: note.trim() || undefined,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8,12,20,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1623",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          maxWidth: 600,
          width: "100%",
          padding: 24,
          color: "#e6edf6",
        }}
      >
        <h3 style={{ marginTop: 0 }}>{entry ? "Eintrag bearbeiten" : "Neuer Plan-Eintrag"}</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <span style={labelStyle}>Datum</span>
            <input type="date" value={dateISO} onChange={(e) => setDateISO(e.target.value)} style={inputStyle} />
          </label>
          <label>
            <span style={labelStyle}>Uhrzeit (optional)</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 12 }}>
          <span style={labelStyle}>Idee (optional)</span>
          <select value={ideaId} onChange={(e) => setIdeaId(e.target.value)} style={inputStyle}>
            <option value="">—</option>
            {ideas.map((i) => (
              <option key={i.id} value={i.id}>
                {i.title}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "block", marginTop: 12 }}>
          <span style={labelStyle}>Storyboard (optional)</span>
          <select
            value={storyboardId}
            onChange={(e) => setStoryboardId(e.target.value)}
            style={inputStyle}
          >
            <option value="">—</option>
            {storyboards.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "block", marginTop: 12 }}>
          <span style={labelStyle}>Reel (optional)</span>
          <select value={reelId} onChange={(e) => setReelId(e.target.value)} style={inputStyle}>
            <option value="">—</option>
            {reels.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
              </option>
            ))}
          </select>
        </label>

        <div style={{ marginTop: 12 }}>
          <span style={labelStyle}>Plattformen</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ALL_PLATFORMS.map((p) => {
              const active = channels.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleChannel(p)}
                  style={{
                    background: active ? PLATFORM_META[p].color : "rgba(255,255,255,0.06)",
                    color: active ? "white" : "inherit",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 999,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  {PLATFORM_META[p].icon} {PLATFORM_META[p].label}
                </button>
              );
            })}
          </div>
        </div>

        <label style={{ display: "block", marginTop: 12 }}>
          <span style={labelStyle}>Notiz</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </label>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
          <div>
            {onDelete ? (
              <button className="btn danger" onClick={onDelete}>
                Löschen
              </button>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn ghost" onClick={onClose}>
              Abbrechen
            </button>
            <button className="btn primary" onClick={submit}>
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = { display: "block", opacity: 0.8, marginBottom: 4, fontSize: 12 };
const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "#0a0f17",
  color: "#e6edf6",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
  fontFamily: "inherit",
};
